import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { superAdminAPI } from "../../services/api";
import { Card, Button, Spinner } from "../UI";
import PasswordInput from "./PasswordInput";
import {
  HiOutlineSparkles,
  HiOutlineChip,
  HiOutlineCheckCircle,
  HiOutlineExclamation,
  HiOutlineLightningBolt,
} from "react-icons/hi";

/**
 * SuperAdmin → Settings → AI Call Analysis
 *
 * Chooses the AI that analyzes call recordings for every organization:
 * Claude (Anthropic, default) or Ollama, plus the Claude model / API key, the
 * Ollama server + model, and whether each falls back to the other on failure.
 * Saved settings apply immediately on the server (no restart).
 */

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-50";

const ProviderCard = ({ active, onClick, icon: Icon, title, subtitle, badge }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-start gap-3 w-full text-left p-4 rounded-xl border transition-colors ${
      active
        ? "bg-purple-50 border-purple-500 shadow-sm"
        : "bg-white border-gray-200 hover:bg-gray-50"
    }`}
  >
    <div
      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        active ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
    </div>
  </button>
);

const StatusLine = ({ ok, children }) => (
  <p className={`flex items-start gap-1.5 text-xs ${ok ? "text-green-700" : "text-amber-700"}`}>
    {ok ? (
      <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0" />
    ) : (
      <HiOutlineExclamation className="w-4 h-4 flex-shrink-0" />
    )}
    <span>{children}</span>
  </p>
);

const AICallAnalysisSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [testing, setTesting] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sa-ai-settings"],
    queryFn: async () => (await superAdminAPI.getAISettings()).data?.data,
  });

  // Initialise the editable form from the saved settings
  useEffect(() => {
    if (!data) return;
    setForm({
      provider: data.provider || data.effectiveProvider || "anthropic",
      anthropicModel: data.anthropic?.model || data.anthropic?.effectiveModel || "",
      ollamaBaseUrl: data.ollama?.baseUrl || "",
      ollamaModel: data.ollama?.model || "",
      ollamaFallback: data.ollamaFallback !== false,
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body) => superAdminAPI.updateAISettings(body),
    onSuccess: (response) => {
      setNewKey("");
      queryClient.setQueryData(["sa-ai-settings"], (prev) => ({
        ...(prev || {}),
        ...(response.data?.data || {}),
      }));
      toast.success("AI settings saved — used for the next call analysis");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to save AI settings"),
  });

  const runTest = async (provider) => {
    setTesting(provider);
    try {
      const response = await superAdminAPI.testAISettings({
        provider,
        model: provider === "anthropic" ? form.anthropicModel : form.ollamaModel || undefined,
      });
      toast.success(response.data?.message || "Connection OK");
    } catch (err) {
      toast.error(err.response?.data?.message || "Connection test failed", { duration: 6000 });
    } finally {
      setTesting(null);
    }
  };

  if (isLoading || (!form && !error)) {
    return (
      <Card padding="p-6" className="flex justify-center">
        <Spinner />
      </Card>
    );
  }
  if (error) {
    return (
      <Card padding="p-6">
        <StatusLine ok={false}>
          {error.response?.data?.message || "Failed to load AI settings"}
        </StatusLine>
      </Card>
    );
  }

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const models = data.anthropic?.models || [];
  const keyConfigured = data.anthropic?.configured;
  const ollamaModels = data.ollamaStatus?.models || [];
  const isDirty =
    newKey.trim() !== "" ||
    form.provider !== (data.provider || data.effectiveProvider || "anthropic") ||
    form.anthropicModel !== (data.anthropic?.model || data.anthropic?.effectiveModel || "") ||
    form.ollamaBaseUrl !== (data.ollama?.baseUrl || "") ||
    form.ollamaModel !== (data.ollama?.model || "") ||
    form.ollamaFallback !== (data.ollamaFallback !== false);

  const save = () => {
    const body = { ...form };
    if (newKey.trim()) body.anthropicApiKey = newKey.trim();
    saveMutation.mutate(body);
  };

  const clearKey = () => {
    if (!window.confirm("Remove the saved Claude API key? Analysis will use the server .env key or Ollama.")) return;
    saveMutation.mutate({ clearAnthropicKey: true });
  };

  return (
    <Card padding="p-4 sm:p-6" className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineSparkles className="w-5 h-5 text-purple-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">AI Call Analysis</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Choose the AI that scores and summarizes call recordings for every organization.
            Changes apply to the next analysis — no server restart needed.
          </p>
        </div>
      </div>

      {/* Provider */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-800">Analysis AI</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ProviderCard
            active={form.provider === "anthropic"}
            onClick={() => set({ provider: "anthropic" })}
            icon={HiOutlineSparkles}
            title="Claude (Anthropic)"
            subtitle="Cloud AI — best analysis quality. Needs a Claude API key."
            badge="Recommended"
          />
          <ProviderCard
            active={form.provider === "ollama"}
            onClick={() => set({ provider: "ollama" })}
            icon={HiOutlineChip}
            title="Ollama"
            subtitle="Self-hosted models on your Ollama server."
          />
        </div>
        {form.provider === "anthropic" && !keyConfigured && !newKey && (
          <StatusLine ok={false}>
            No Claude API key yet — analysis will use Ollama until you add one below.
          </StatusLine>
        )}
      </div>

      {/* Claude */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <HiOutlineSparkles className="w-4 h-4 text-purple-600" /> Claude settings
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ai-claude-model">
              Claude model
            </label>
            <select
              id="ai-claude-model"
              className={inputClass}
              value={form.anthropicModel}
              onChange={(e) => set({ anthropicModel: e.target.value })}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.note}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ai-claude-key">
              Claude API key
            </label>
            <PasswordInput
              id="ai-claude-key"
              className={inputClass}
              value={newKey}
              autoComplete="off"
              placeholder={
                data.anthropic?.keySet
                  ? `Saved: ${data.anthropic.keyMasked} — enter a new key to replace`
                  : "sk-ant-…"
              }
              onChange={(e) => setNewKey(e.target.value)}
            />
            <div className="mt-1.5 space-y-1">
              {data.anthropic?.keySet ? (
                <StatusLine ok>
                  Key saved ({data.anthropic.keyMasked}), stored encrypted.{" "}
                  <button type="button" onClick={clearKey} className="underline text-red-600">
                    Remove
                  </button>
                </StatusLine>
              ) : data.anthropic?.envKeySet ? (
                <StatusLine ok>Using the key from the server .env.</StatusLine>
              ) : null}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => runTest("anthropic")}
          loading={testing === "anthropic"}
          disabled={!!testing || (!keyConfigured && !newKey) || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Test Claude connection
        </Button>
      </div>

      {/* Ollama */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <HiOutlineChip className="w-4 h-4 text-gray-600" /> Ollama settings
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ai-ollama-url">
              Ollama server URL
            </label>
            <input
              id="ai-ollama-url"
              className={inputClass}
              value={form.ollamaBaseUrl}
              placeholder={data.ollama?.effectiveBaseUrl || "http://localhost:11434"}
              onChange={(e) => set({ ollamaBaseUrl: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ai-ollama-model">
              Ollama model
            </label>
            <input
              id="ai-ollama-model"
              className={inputClass}
              list="ai-ollama-models"
              value={form.ollamaModel}
              placeholder={data.ollama?.effectiveModel || "llama3.1"}
              onChange={(e) => set({ ollamaModel: e.target.value })}
            />
            <datalist id="ai-ollama-models">
              {ollamaModels.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </div>
        </div>
        {data.ollamaStatus?.running ? (
          <StatusLine ok={ollamaModels.length > 0}>
            Ollama server reachable —{" "}
            {ollamaModels.length ? `models: ${ollamaModels.join(", ")}` : "no models installed"}
          </StatusLine>
        ) : (
          <StatusLine ok={false}>Ollama server not reachable at {data.ollama?.effectiveBaseUrl}</StatusLine>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => runTest("ollama")}
          loading={testing === "ollama"}
          disabled={!!testing || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Test Ollama connection
        </Button>
      </div>

      {/* Fallback */}
      <div className="border-t border-gray-100 pt-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            checked={form.ollamaFallback}
            onChange={(e) => set({ ollamaFallback: e.target.checked })}
          />
          <span>
            <span className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
              <HiOutlineLightningBolt className="w-4 h-4 text-amber-500" />
              Use the other AI as a backup
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">
              If {form.provider === "ollama" ? "Ollama" : "Claude"} fails, the call is analyzed
              with {form.provider === "ollama" ? "Claude" : "Ollama"} instead.
            </span>
          </span>
        </label>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-gray-100 pt-5">
        <p className="text-xs text-gray-400">
          {data.updatedAt
            ? `Last changed ${new Date(data.updatedAt).toLocaleString()}${data.updatedBy ? ` by ${data.updatedBy}` : ""}`
            : "Using server defaults"}
        </p>
        <Button onClick={save} loading={saveMutation.isPending} disabled={!isDirty || saveMutation.isPending}>
          Save AI settings
        </Button>
      </div>
    </Card>
  );
};

export default AICallAnalysisSettings;
